import React, { useContext, useEffect, useMemo } from 'react'
import { ArcLayer } from 'deck.gl'
import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { MapContext } from './mapContext'
import chroma from 'chroma-js'
import { useTheme } from 'styled-components'
import { FeatureCollectionWithInfoAndStyle } from '~/components/mapContent/utils/geoAndStatsToFeatureCollection'
import { truthy } from '~/prix'
import { alertColors } from '~/packages/general/variable/spaceTime.data'

interface ArcProps {
  geoJson: FeatureCollectionWithInfoAndStyle
  origin?: 'previous' | 'first'
  widthMultiplier?: number
}

interface Arc {
  fromCoordinates: [number, number]
  toCoordinates: [number, number]
  width: number
  name?: string
  fromColor: [number, number, number]
  toColor: [number, number, number]
}

export default function Arc({ geoJson, origin = 'first', widthMultiplier = 4 }: ArcProps) {
  const theme = useTheme()

  const mapContext = useContext(MapContext)
  if (!mapContext) {
    throw new Error('Marker must be used within a Map')
  }

  const { map, google, is3D } = mapContext

  const arcs: Arc[] = useMemo(
    () =>
      geoJson.features
        .map((feature, index, array) => {
          if (index === 0) {
            return null
          }
          const from = array[origin === 'first' ? 0 : index - 1]
          const to = feature

          const fromCoordinates = from.geometry.coordinates as [number, number]
          const toCoordinates = to.geometry.coordinates as [number, number]
          const width = (to.properties.value ?? 1) * widthMultiplier
          const name = `${from.properties.name} → ${to.properties.name}`

          const fromColorString = from.properties.alert
            ? alertColors[from.properties.alert]
            : theme.colors.primary
          const fromColor = chroma(fromColorString).rgb()

          const toColorString = to.properties.alert
            ? alertColors[to.properties.alert]
            : theme.colors.secondary
          const toColor = chroma(toColorString).rgb()
          return { fromCoordinates, toCoordinates, width, name, fromColor, toColor }
        })
        .filter(truthy),
    [geoJson, origin, widthMultiplier, theme.colors.primary, theme.colors.secondary],
  )

  useEffect(() => {
    if (!map || !google) {
      return
    }

    const layer = new ArcLayer<Arc>({
      id: 'arc-layer',
      data: arcs,
      getSourcePosition: arc => arc.fromCoordinates,
      getTargetPosition: arc => arc.toCoordinates,
      getSourceColor: arc => arc.fromColor,
      getTargetColor: arc => arc.toColor,
      getWidth: arc => arc.width,
      pickable: true,
    })

    const overlay = new GoogleMapsOverlay({
      layers: [layer],
      getTooltip: info => {
        const object = info.object as Arc | undefined
        return object?.name ?? null
      },
    })

    overlay.setMap(map)

    return () => {
      overlay.setMap(null)
    }
  }, [map, google, arcs, is3D])

  return null
}
